import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Clock, Phone, Mail, Send, Navigation, Car } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { SITE } from "@/lib/config";
import { useLang } from "@/lib/i18n";
import { breadcrumbSchema, restaurantSchema } from "@/lib/schema";
import { Seo } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { OrderButton, CallLink, DirectionsLink } from "@/components/TrackedLinks";
import { FacebookIcon, InstagramIcon } from "@/components/SocialIcons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "bg-white border-hacienda-border-light focus-visible:ring-hacienda-terracotta h-11";

const Row = ({ icon: Icon, title, children, testId }) => (
  <div className="flex items-start gap-4" data-testid={testId}>
    <div className="bg-hacienda-terracotta/10 p-3.5 rounded-xl flex-shrink-0"><Icon size={22} className="text-hacienda-terracotta" aria-hidden="true" /></div>
    <div className="min-w-0">
      <h3 className="font-semibold text-hacienda-charcoal text-lg mb-1">{title}</h3>
      <div className="text-hacienda-gray">{children}</div>
    </div>
  </div>
);

const ContactForm = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t("visit.sent"));
      setForm({ firstName: "", lastName: "", email: "", message: "" });
    } catch {
      toast.error(t("visit.failed"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5"><Label htmlFor="firstName">{t("visit.firstName")} *</Label><Input id="firstName" name="firstName" required autoComplete="given-name" value={form.firstName} onChange={update} className={inputCls} data-testid="contact-firstname-input" /></div>
        <div className="space-y-1.5"><Label htmlFor="lastName">{t("visit.lastName")} *</Label><Input id="lastName" name="lastName" required autoComplete="family-name" value={form.lastName} onChange={update} className={inputCls} data-testid="contact-lastname-input" /></div>
      </div>
      <div className="space-y-1.5"><Label htmlFor="email">{t("visit.email")} *</Label><Input id="email" name="email" type="email" required autoComplete="email" value={form.email} onChange={update} className={inputCls} data-testid="contact-email-input" /></div>
      <div className="space-y-1.5"><Label htmlFor="message">{t("visit.message")} *</Label><Textarea id="message" name="message" required rows={5} value={form.message} onChange={update} className="bg-white border-hacienda-border-light focus-visible:ring-hacienda-terracotta resize-none" data-testid="contact-message-input" /></div>
      <button type="submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-hacienda-terracotta hover:bg-hacienda-terracotta-dark disabled:opacity-60 text-white py-3.5 font-medium transition-colors" data-testid="contact-submit-btn">
        {busy ? t("actions.sending") : <><Send size={18} aria-hidden="true" /> {t("visit.send")}</>}
      </button>
    </form>
  );
};

export const VisitPage = () => {
  const { t } = useLang();
  return (
    <div data-testid="visit-page">
      <Seo
        title="Tijuas Tacos Birrieria | 5505 Osuna Rd NE Albuquerque"
        description="Visit Tijuas Tacos Birrieria at 5505 Osuna Rd NE, Albuquerque, NM 87109. Hours, directions, phone (505) 417-3431, online ordering and contact form."
        path="/visit-us"
        jsonLd={[restaurantSchema(), breadcrumbSchema([{ label: t("nav.visit"), to: "/visit-us" }])]}
      />
      <Breadcrumbs items={[{ label: t("nav.visit"), to: "/visit-us" }]} />

      <section className="py-16 px-4" data-testid="contact-hero">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-hacienda-terracotta text-sm uppercase tracking-[0.2em] mb-4">{t("visit.eyebrow")}</p>
          <h1 className="font-serif text-5xl md:text-6xl text-hacienda-charcoal font-semibold mb-5">{t("visit.title")}</h1>
          <p className="text-hacienda-gray text-lg max-w-2xl mx-auto">{t("visit.sub")}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <OrderButton location="visit_hero" data-testid="contact-order-btn" />
            <DirectionsLink location="visit_hero" data-testid="map-directions-btn"><Navigation size={18} aria-hidden="true" /> {t("actions.directions")}</DirectionsLink>
            <CallLink location="visit_hero" data-testid="contact-phone-link"><Phone size={18} aria-hidden="true" /> {t("actions.call")}</CallLink>
          </div>
        </div>
      </section>

      <section className="pb-20 px-4" data-testid="contact-main-section">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 lg:gap-20">
          <div className="space-y-8">
            <Row icon={MapPin} title={SITE.name} testId="contact-address-link">
              <address className="not-italic">{SITE.address.street}<br />{SITE.address.city}, {SITE.address.state} {SITE.address.zip}</address>
            </Row>
            <Row icon={Clock} title={t("visit.hours")} testId="contact-hours">
              <dl className="space-y-1">
                <div className="flex justify-between gap-8"><dt>{t("days.tueThu")}</dt><dd className="font-medium">11:00 a.m. – 9:00 p.m.</dd></div>
                <div className="flex justify-between gap-8"><dt>{t("days.friSat")}</dt><dd className="font-medium">11:00 a.m. – 10:00 p.m.</dd></div>
                <div className="flex justify-between gap-8"><dt>{t("days.sunMon")}</dt><dd className="font-medium text-hacienda-terracotta">{t("days.closed")}</dd></div>
              </dl>
            </Row>
            <Row icon={Phone} title={t("visit.phone")}>
              <a href={`tel:${SITE.phoneE164}`} className="hover:text-hacienda-terracotta text-lg">{SITE.phoneDisplay}</a>
            </Row>
            <Row icon={Mail} title={t("visit.email")}>
              <a href={`mailto:${SITE.email}`} className="hover:text-hacienda-terracotta break-all" data-testid="contact-email-link">{SITE.email}</a>
            </Row>
            <Row icon={Car} title={t("visit.parking")} testId="contact-parking">
              <p>{t("visit.parkingNote")}</p>
            </Row>
            <div className="flex items-center gap-4 pt-2">
              <span className="text-hacienda-gray font-medium">{t("visit.follow")}:</span>
              <a href={SITE.facebook} target="_blank" rel="noopener noreferrer" className="bg-hacienda-terracotta/10 p-3 rounded-full hover:bg-hacienda-terracotta hover:text-white text-hacienda-terracotta transition-colors" aria-label="Facebook" data-testid="contact-facebook-link"><FacebookIcon size={20} /></a>
              <a href={SITE.instagram} target="_blank" rel="noopener noreferrer" className="bg-hacienda-terracotta/10 p-3 rounded-full hover:bg-hacienda-terracotta hover:text-white text-hacienda-terracotta transition-colors" aria-label="Instagram" data-testid="contact-instagram-link"><InstagramIcon size={20} /></a>
            </div>
          </div>

          <div className="bg-hacienda-cream rounded-2xl p-7 sm:p-9">
            <h2 className="font-serif text-3xl text-hacienda-charcoal font-semibold mb-2">{t("visit.formTitle")}</h2>
            <p className="text-hacienda-gray mb-7">{t("visit.formSub")}</p>
            <ContactForm />
          </div>
        </div>
      </section>

      <section className="bg-hacienda-cream py-16 px-4" data-testid="map-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-serif text-3xl text-hacienda-charcoal font-semibold mb-6 text-center">{t("visit.map")}</h2>
          <div className="rounded-2xl overflow-hidden shadow-lg border border-hacienda-border-light">
            <iframe src={SITE.mapsEmbedUrl} width="100%" height="420" style={{ border: 0 }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" title={`${SITE.name} location map`} className="w-full" data-testid="google-maps-embed" />
          </div>
          <div className="text-center mt-6">
            <DirectionsLink location="map" variant="primary"><Navigation size={18} aria-hidden="true" /> {t("actions.directions")}</DirectionsLink>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VisitPage;
